// Client-side duplicate resolution for CSV import. A "file record" is the DB-shaped
// object produced by a target's toRecord(); an "existing row" is the current DB row
// fetched for a matched duplicate. We decide the merge in JS (not via a raw upsert)
// so list columns (phone_numbers, visit_dates) can be UNIONED — a PostgREST upsert
// can only overwrite a column, never append to it.

function isEmpty(v) {
  if (v == null) return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.keys(v).length === 0;
  return String(v).trim() === '';
}

// Scalar comparison key: trimmed + lowercased so "JANE@OLD" == "jane@old". Values
// reaching here are already importer-normalized (phones bare, dates YYYY-MM-DD).
function normScalar(v) {
  return isEmpty(v) ? '' : String(v).trim().toLowerCase();
}

function asList(v) {
  if (v == null) return [];
  return Array.isArray(v) ? v : [v];
}

// Union preserving existing-first order, de-duplicated by string identity.
function unionList(existingVal, fileVal) {
  const seen = new Set();
  const out = [];
  [...asList(existingVal), ...asList(fileVal)].forEach((item) => {
    const k = String(item);
    if (!seen.has(k)) { seen.add(k); out.push(item); }
  });
  return out;
}

// A list column "changed" iff the file contributes a value not already present.
function listGrows(existingVal, fileVal) {
  const have = new Set(asList(existingVal).map(String));
  return asList(fileVal).some((item) => !have.has(String(item)));
}

/**
 * Classify how a file record differs from the existing DB row.
 * @returns {{ newColumns: string[], changedColumns: string[] }}
 *   newColumns     — file columns NOT on the table (would be created), with a value.
 *   changedColumns — table columns whose file value differs from the stored value.
 */
export function classifyDifferences(existingRow, fileRecord, { tableColumns, listColumns = [], keyColumn }) {
  const onTable = new Set(tableColumns);
  const isList = new Set(listColumns);
  const newColumns = [];
  const changedColumns = [];
  Object.keys(fileRecord).forEach((key) => {
    if (key === keyColumn) return;
    const fileVal = fileRecord[key];
    if (isEmpty(fileVal)) return;               // file has nothing to contribute here
    if (!onTable.has(key)) { newColumns.push(key); return; }
    if (isList.has(key)) {
      if (listGrows(existingRow[key], fileVal)) changedColumns.push(key);
    } else if (normScalar(fileVal) !== normScalar(existingRow[key])) {
      changedColumns.push(key);
    }
  });
  return { newColumns, changedColumns };
}

/**
 * Build the record to upsert for a duplicate, per the chosen action. Starts from a
 * FULL copy of the existing DB row (fetched via select('*')) so no stored column is
 * ever dropped — a dropped column would be nulled out by a heterogeneous batch
 * upsert. Then applies the action:
 *   - Merge / Both add the new columns from the file.
 *   - Replace / Both overwrite the changed old columns from the file (list columns
 *     are UNIONED with the existing values, so history is never lost).
 *   - Unchanged columns keep their existing value.
 * (Skip is never built — the caller omits skipped duplicates entirely.)
 *
 * IMPORTANT: because the record carries every existing column — including NOT NULL
 * server-managed ones (created_at, meds, …) — callers MUST upsert these records in
 * their OWN batch (all conflict → all UPDATE), separate from brand-new INSERTs, or
 * the missing columns on the new rows would violate NOT NULL. See doImport's two-pass
 * commit. On an all-UPDATE batch, re-writing a server-managed column to its existing
 * value is a harmless no-op.
 */
export function buildMergedRecord(existingRow, fileRecord, action, { newColumns, changedColumns, listColumns = [], keyColumn }) {
  const isList = new Set(listColumns);
  const addNew = action === 'merge' || action === 'both';
  const doReplace = action === 'replace' || action === 'both';
  const out = { ...existingRow };
  if (addNew) newColumns.forEach((key) => { out[key] = fileRecord[key]; });
  if (doReplace) {
    changedColumns.forEach((key) => {
      out[key] = isList.has(key) ? unionList(existingRow[key], fileRecord[key]) : fileRecord[key];
    });
  }
  out[keyColumn] = existingRow[keyColumn] ?? fileRecord[keyColumn];
  return out;
}
